import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { LOG_TYPES } from '@/app/common/constants'

import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'

// Import or create a Modal component
import { LogUserActivity } from '@/app/apis/users/requests'
import Image from '@/app/common/components/Image'
import MultiQueryPageLoader from '@/app/common/components/MultiQueryPageLoader'
import { ProviderQueries } from '@/queries/providers/queries'
import { StateQueries } from '@/queries/states/queries'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useSelector } from 'react-redux'
import BusinessDetailsCard from './components/BusinessDetails'
import ColorSelector from './components/ColorSelector'
import CompanyInfoCard from './components/CompanyInfoCard'
import ContactInfoCard from './components/ContactInfo'
import LocationInfoCard from './components/LocationInformation'
import LogoSettings from './components/LogoSettings'
import MarketplaceSettings from './components/MarketplaceSettings'
import UnpublishedChangesBanner from './components/unPublishedChangesBanner'
import AffiliationsCard from './components/AffiliationsCard'
// import BusinessInfo from './components/BusinessLocationDetails'
// import SchoolForm from '@/pages/providers/components/SchoolForm/page.tsx'
// import Image from '@/app/common/components/Image'

// Section IDs for scrolling/navigation
// eslint-disable-next-line
export const SECTION_IDS = {
  LOGO_SETTINGS: 'logo-settings',
  AFFILIATIONS: 'affiliations',
  MARKETPLACE_SETTINGS: 'marketplace-settings',
  COLOR_SETTINGS: 'color-settings',
  COMPANY_INFORMATION: 'company-information',
  BUSINESS_DETAILS: 'business-details',
  LOCATION_INFORMATION: 'location-info',
  CONTACT_INFORMATION: 'contact-info',
  PROVIDER_DETAILS: 'provider-details',
}

const ConfigureStorePage = () => {
  const { providerId } = useParams()
  if (!providerId) {
    throw new Error('ProviderId Not found')
  }
  const programId = useSelector(selectSelectedProgram).id

  const providerQuery = useQuery(
    ProviderQueries.getProviderPreviewSettings(programId, providerId)
  )

  const statesQuery = useQuery(StateQueries.getStateByCountry(3))

  const providerDetailQuery = useQuery(
    ProviderQueries.getProviderById(programId, providerId)
  )

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'configure store page',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  const scrollToSectionWithOffset = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 68 // 63px header
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  return (
    <>
      <MultiQueryPageLoader
        queries={[providerQuery, statesQuery, providerDetailQuery] as const}
      >
        {([provider, states, providerDetail]) => (
          <div>
            <div className="flex w-full max-w-5xl mx-auto mb-4">
              <UnpublishedChangesBanner
                providerPreviewInformation={provider}
                providerId={providerId}
                programId={programId}
              />
            </div>
            <div className="flex w-full max-w-5xl mx-auto">
              {/* Sidebar */}
              <div className="w-64 pr-6 pt-2">
                <Card className="sticky top-24">
                  <CardHeader className="py-2 font-semibold text-gray-800 ">
                    Settings
                  </CardHeader>
                  <CardContent>
                    <nav className="space-y-2">
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.LOGO_SETTINGS
                              )
                            }
                          >
                            Logo & Info
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.AFFILIATIONS
                              )
                            }
                          >
                            Affiliations
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.MARKETPLACE_SETTINGS
                              )
                            }
                          >
                            Marketplace Settings
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.COLOR_SETTINGS
                              )
                            }
                          >
                            Color Settings
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.COMPANY_INFORMATION
                              )
                            }
                          >
                            Company Information
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.BUSINESS_DETAILS
                              )
                            }
                          >
                            Business Details
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.LOCATION_INFORMATION
                              )
                            }
                          >
                            Location Information
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.CONTACT_INFORMATION
                              )
                            }
                          >
                            Contact Information
                          </button>
                        </li>
                        <li>
                          <button
                            className="text-left w-full hover:text-primary transition-colors"
                            onClick={() =>
                              scrollToSectionWithOffset(
                                SECTION_IDS.PROVIDER_DETAILS
                              )
                            }
                          >
                            Details
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </CardContent>
                </Card>
              </div>
              {/* Main Content */}
              <div className="flex-1 space-y-10">
                <div id={SECTION_IDS.LOGO_SETTINGS}>
                  <Card>
                    <CardContent className="flex items-center gap-4 justify-between">
                      <div className="flex items-center gap-2">
                        {provider.logo ? (
                          <Image
                            key={provider.logo}
                            allowZoom={false}
                            path={provider.logo}
                            className="h-24 w-24 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-24 w-24 text-sm font-medium text-gray-600 border-gray-200 border rounded-full bg-gray-100 flex items-center justify-center">
                            No Logo
                          </div>
                        )}
                        <div>
                          <div className="font-semibold">{provider?.name}</div>
                          <div className="text-sm text-gray-600">
                            {provider?.city}
                          </div>
                        </div>
                      </div>
                      <LogoSettings
                        providerId={providerId}
                        programId={programId}
                        onUpdateLogoSuccess={() => {}}
                        provider={provider}
                      />
                    </CardContent>
                  </Card>
                </div>
                <div id={SECTION_IDS.AFFILIATIONS}>
                  <AffiliationsCard
                    affiliations={providerDetail.affiliations}
                  />
                </div>
                <div id={SECTION_IDS.MARKETPLACE_SETTINGS}>
                  <MarketplaceSettings
                    providerId={providerId}
                    provider={provider}
                  />
                </div>
                <div id={SECTION_IDS.COLOR_SETTINGS}>
                  <ColorSelector
                    providerId={providerId}
                    initialColor={provider.header_background_color ?? '#000'}
                  />
                </div>
                <div id={SECTION_IDS.COMPANY_INFORMATION}>
                  <CompanyInfoCard
                    provider={provider}
                    providerId={providerId}
                  />
                </div>
                <div
                  className="business-details"
                  id={SECTION_IDS.BUSINESS_DETAILS}
                >
                  <BusinessDetailsCard
                    provider={provider}
                    providerId={providerId}
                  />
                </div>
                <div id={SECTION_IDS.LOCATION_INFORMATION}>
                  <LocationInfoCard
                    provider={provider}
                    providerId={providerId}
                    states={states}
                  />
                </div>
                <div id={SECTION_IDS.CONTACT_INFORMATION}>
                  <ContactInfoCard
                    providerId={providerId}
                    provider={provider}
                  />
                </div>

                {/* <div id={SECTION_IDS.PROVIDER_DETAILS}>
                {
                  <BusinessInfo
                  providerId={providerId}
                  providerDetails={provider}
                  />
                  }
                  </div> */}
                {/* <div id={SECTION_IDS.PROVIDER_DETAILS}>
                {provider?.school_application && (
                  <SchoolForm
                  providerInfo={provider}
                  schoolInfo={provider.school_application}
                  />
                  )}
                  </div> */}
              </div>
            </div>
          </div>
        )}
      </MultiQueryPageLoader>
    </>
  )
}

export default ConfigureStorePage
